import { MetaData } from './../tookan/_interface/task';
import { SHELL_APP_CONFIG } from '../_interface/shellAppConfig';
import { OrderStatus } from './../_interface/order';
import { Order } from 'src/app/core/_interface/order';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, of, Subject, Subscription, interval, Observable, EMPTY, timer, race, observable } from 'rxjs';
import { map, switchMap, skip, tap, catchError, concatMap, take, retry, startWith, takeWhile, repeat } from 'rxjs/operators';
import { WebSocketSubject } from 'rxjs/webSocket';
import { environment } from 'src/environments/environment';
import { Waiter } from '../_interface/user';
import { DeviceService } from './device.service';
import { NGXLogger } from 'ngx-logger';
import { deriveBackendOrigin, deriveNaturalBackendOrigin, setBackendServerOverride, clearBackendServerOverride, hasBackendServerOverride } from '../_helpers/derive-origin';
import { SystemStatusService } from './system-status.service';
import { CrossAppMessagingService } from './crossApp-messaging.service';
import { isNativeShell } from '../_helpers/native-shell';
import { getBrowserInfo } from '../_helpers/browser-info';
import { WsBridgeCapabilities, WsBridgeState, WsBridgeTransportCache, WS_BRIDGE_CACHE_KEY } from '../_interface/wsBridge';

export interface WebsocketData {
  type: string;
  data: any;
  requestID?: string;
}

export interface WebsocketResult {
  success?: boolean;
  data?: any;
  requestID?: string;
}

export interface WebsocketResultOrderData {
  action: string;
  order: Order
}

export interface WebsocketResultOrderStatus {
  newStatus: OrderStatus;
  dueDate: Date,
  orderID: number;
  waiter: Waiter;
}

export interface WebsocketResultOrderPaid {
  orderID: number;
}


// Heartbeat configuration
const HEARTBEAT_INTERVAL = 10000;
const HEARTBEAT_TIMEOUT = 5000;

// After this many consecutive failed (re)connect attempts to a backend we were redirected to
// (via the WS auth `server` field), abandon it and fall back to the app's original/natural
// server — the automatic equivalent of the manual page reload that recovery used to require.
const MAX_SERVER_SWITCH_RETRIES = 5;

// Osigurač protiv petlje prebacivanja (npr. dva servera sa različitim podešavanjem firme):
// najviše ovoliko prebacivanja u prozoru — posle toga se `server` iz auth-a ignoriše do isteka prozora.
const MAX_SERVER_SWITCHES_PER_WINDOW = 4;
const SERVER_SWITCH_WINDOW_MS = 60000;


@Injectable({
  providedIn: 'root'
})
export class WSService {
  private connection: WebSocketSubject<WebsocketData> | null = null;
  heartbeat$?: Observable<any>;
  heartbeatSubscription?: Subscription;
  pong$ = new Subject<number>();
  missedPongResponse = 0;

  logPing = false;

  connected = new BehaviorSubject<boolean>(false);
  socketID = "";
  serverID = "";

  /**
   * Firma na koju je OVA konekcija vezana, iz echo-a u odgovoru na `setCompany`. Namerno se ne
   * puni iz SessionService-a: odgovor servera je jedini autoritativan izvor — kaze sta je server
   * zaista primenio, a ne sta je trazeno. Isto kao sto `serverID` stize iz `connected` frame-a.
   */
  company$ = new BehaviorSubject<{ id: number; name: string }>({ id: 0, name: '' });
  private debugEl: HTMLElement | null = null;
  connectRetryCount = new BehaviorSubject<number>(0);
  authenticated = new BehaviorSubject<boolean>(false);
  retryCountdown$?: Subscription;
  secondsToRetry = 0;

  // Guards the deferred fall-back-to-original-server action (see revertToOriginalServer).
  private revertPending = false;

  // Vremena nedavnih prebacivanja servera (osigurač protiv petlje, vidi allowServerSwitch).
  private serverSwitchTimes: number[] = [];

  connectionPending = false;
  elapsedFromLastConnection$?: Subscription;
  elapsedFromLastConnection = 0;

  private subscription?: Subscription;

  private subscriptions: { [key: string]: Subject<WebsocketResult> } = {};

  // ---- Native bridge transport (MAUI shell poseduje socket; vidi _interface/wsBridge.ts) ----
  // 'web' = postojeći WebSocketSubject u browseru/WebView-u; 'bridge' = frame-ovi idu kroz
  // crossAppMessaging do native ClientWebSocket-a. Odluka pada u decideTransport().
  private transportMode: 'undecided' | 'probing' | 'bridge' | 'web' = 'undecided';
  private crossApp = inject(CrossAppMessagingService);
  private lastBridgeState: WsBridgeState | null = null;
  private bridgeAttachPending = false;
  private bridgeConnectFailures = 0;
  private bridgeInitialized = false;

  constructor(private ds: DeviceService,
    private logger: NGXLogger
  ) {
    //  this.runSleepModeDetection();

    // Mreža se vratila — novi pokušaj odmah, ne posle backoff-a (do 30 s); inače bi i status veze
    // na loginu toliko još tvrdio da server ne radi. Samo web transport: native socket sam prati mrežu.
    window.addEventListener('online', () => {
      if (!this.connected.value && this.transportMode === 'web') {
        this.logger.info('Network back online - reconnecting WS now');
        this.reconnect();
      }
    });

    this.connected
      .pipe(
        skip(1)
      )
      .subscribe(
        connected => {
          if (!connected) {
            this.authenticated.next(false);
          } else {
            this.sendVersion();
            this.sendDeviceID();
          }
        }
      )


    this.observe("auth").subscribe(
      msg => {
        if (msg.success) {
          // If the auth response tells us to use a different backend server, tear down
          // and reconnect to it WITHOUT marking this connection as authenticated.
          if (this.handleServerSwitch(msg.data?.server)) {
            return;
          }
          this.authenticated.next(true);
        }
      }
    )

    this.observe('setCompany').subscribe(
      msg => {
        if (msg.success && msg.data) {
          this.company$.next({ id: msg.data.companyID ?? 0, name: msg.data.companyName ?? '' });
        }
      }
    )

    this.observe("connected").subscribe(
      msg => {
        this.socketID = msg.data?.socketID ?? msg.data;
        this.serverID = msg.data?.serverID ?? "";
        this.logger.info('connected to WS - socketID: ' + this.socketID + ' serverID: ' + this.serverID);
        this.showServerID(this.serverID);
        this.connected.next(true);
      }
    )

    this.observe("reload").subscribe(() => location.reload());

    this.observe('pong').subscribe(
      (msg) => {
        this.missedPongResponse = 0;
        if (msg.data) {
          var diff = new Date().getTime() - msg.data;

          if (diff > HEARTBEAT_TIMEOUT) {
            this.logger.warn("Late ping response. Delta: " + diff);
          }

          this.pong$.next(msg.data); // Emit event when "pong" is received
        }
      }
    );

    // Auto fall-back: if the WS auth redirected us to another backend (handleServerSwitch) and
    // that server then becomes unreachable, return to the original server after
    // MAX_SERVER_SWITCH_RETRIES failed attempts instead of needing a manual app refresh.
    // connectRetryCount is fed by BOTH transports — the web closeObserver and the native
    // bridge's reported retryCount — so this single guard covers web and native-owned sockets.
    this.connectRetryCount.subscribe(count => {
      if (count >= MAX_SERVER_SWITCH_RETRIES && hasBackendServerOverride()) {
        this.revertToOriginalServer('unreachable');
      }
    });
  }


  sendVersion() {
    // Browser/engine se šalju na svakoj konekciji jer se uređaj u međuvremenu može nadograditi
    // (Android system WebView, novi Chrome). Backend uvek prepisuje zatečenu vrednost.
    const browser = getBrowserInfo();

    var data: WebsocketData = {
      type: 'version',
      data: {
        deviceID: this.ds.deviceID,
        appVersion: environment.appVersion,
        nativeAppVersion: SHELL_APP_CONFIG.appVersion,
        cardPaymentProvider: SHELL_APP_CONFIG.cardPaymentProvider,
        hasPrinter: SHELL_APP_CONFIG.hasPrinter,
        nativeApp: isNativeShell(),
        browser: browser.browser,
        browserVersion: browser.browserVersion,
        engineVersion: browser.engineVersion,
        userAgent: browser.userAgent
      }
    }

    this.send(data);
  }

  sendDeviceID() {
    var data: WebsocketData = {
      type: 'udid',
      data: this.ds.deviceID
    }

    this.send(data);
  }

  startNoConnectionTimer() {
    this.elapsedFromLastConnection$?.unsubscribe();

    this.elapsedFromLastConnection$ = interval(1000)
      .subscribe(
        () => this.elapsedFromLastConnection += 1
      );
  }

  stopNoConnectionTimer() {
    this.elapsedFromLastConnection$?.unsubscribe();
  }

  connect() {
    // App-ovi koji ne koriste WS (npr. delivery) postave environment.disableWebsocket:true.
    // Socket se tada nikad ne otvara; observe() pretplate ostaju validne ali nikad ne emituju.
    // Čita se preko (as any) da ostane bezbedno za app-ove čiji environment nema to polje.
    if ((environment as any).disableWebsocket === true) {
      return;
    }
    switch (this.transportMode) {
      case 'web':
        this.connectWeb();
        return;
      case 'bridge':
        this.bridgeAttach();
        return;
      case 'probing':
        return; // odluka u toku — connect() se ponovo poziva kad probe završi
      case 'undecided':
        this.decideTransport();
        return;
    }
  }

  // Odluka o transportu. NE čita environment.nativeApp — WSService se konstruiše pre
  // SetEnvironmentFlags APP_INITIALIZER-a (ctor poziva observe() -> connect()), pa bi
  // flag u tom trenutku uvek bio false. isNativeShell() čita URL/localStorage direktno.
  private decideTransport() {
    if (!isNativeShell()) {
      this.transportMode = 'web';
      this.connectWeb();
      return;
    }

    const cache = this.readTransportCache();
    if (cache?.supported === false) {
      // Stari APK bez bridge-a: bez boot penala — web odmah, a probe u pozadini
      // osvežava keš (konvergira na bridge posle APK update-a, od sledećeg reload-a).
      this.transportMode = 'web';
      this.connectWeb();
      setTimeout(() => this.probeCapabilities().then(caps => this.writeTransportCache(caps)), 0);
      return;
    }

    this.transportMode = 'probing';

    // Macrotask: probe se šalje tek kad se bootstrap slegne (WSService se konstruiše tokom DI-ja).
    // Detekciju shell-a radi isNativeShell() (i ovde i u crossApp guard-u), pa probe ne zavisi
    // od toga da li je SetEnvironmentFlags već postavio environment.nativeApp.
    setTimeout(async () => {
      const caps = await this.probeCapabilities();
      this.writeTransportCache(caps);

      if (caps?.ws === true) {
        this.logger.info('WS transport: native bridge (APK ' + caps.nativeAppVersion + ')');
        this.transportMode = 'bridge';
        this.initBridge();
      } else {
        this.logger.info('WS transport: web (native bridge unavailable)');
        this.transportMode = 'web';
      }

      this.connect();
    }, 0);
  }

  // Stari APK odgovara na nepoznatu akciju sa response(null) za par ms — timeout od 3 s
  // je samo osigurač za pra-stare/pokvarene bridge-ove.
  private async probeCapabilities(): Promise<WsBridgeCapabilities | null> {
    try {
      const caps = await this.crossApp.sendRawMessage({ action: 'ws.getCapabilities' }, true, 3000, true);
      return caps?.ws === true ? caps as WsBridgeCapabilities : null;
    } catch {
      return null;
    }
  }

  private readTransportCache(): WsBridgeTransportCache | null {
    try {
      const raw = localStorage.getItem(WS_BRIDGE_CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private writeTransportCache(caps: WsBridgeCapabilities | null) {
    try {
      const cache: WsBridgeTransportCache = {
        supported: caps?.ws === true,
        nativeAppVersion: caps?.nativeAppVersion,
        at: new Date().toISOString()
      };
      localStorage.setItem(WS_BRIDGE_CACHE_KEY, JSON.stringify(cache));
    } catch { }
  }

  private initBridge() {
    if (this.bridgeInitialized) {
      return;
    }
    this.bridgeInitialized = true;

    // Svaki inbound frame sa native socketa — isti routing kao kod web transporta.
    this.crossApp.onMessage('ws.message').subscribe(d => {
      try {
        const frame = (typeof d === 'object' && d !== null) ? d : this.crossApp.stringToJsonObject(d as any);
        this.onMessageReceived(frame);
      } catch {
        this.logger.error('ws.message parse error', d);
      }
    });

    this.crossApp.onMessage('ws.state').subscribe(s => {
      try {
        const state = ((typeof s === 'object' && s !== null) ? s : this.crossApp.stringToJsonObject(s as any)) as WsBridgeState;
        this.onBridgeState(state, true);
      } catch {
        this.logger.error('ws.state parse error', s);
      }
    });
  }

  // Zakači se na native socket. Idempotentno: native sam auto-reconnect-uje, a socket
  // preživljava reload (ista adresa = re-attach na živu konekciju, ne nova konekcija).
  private async bridgeAttach() {
    if (this.bridgeAttachPending) {
      return;
    }
    if (this.lastBridgeState && this.lastBridgeState.state !== 'disconnected') {
      return; // već connected/connecting — observe() zove connect() pri svakoj pretplati
    }

    this.bridgeAttachPending = true;
    this.startNoConnectionTimer();

    const url = this.buildWsUrl();
    this.logger.debug('WS bridge connecting to: ' + url);

    try {
      // Bez attachId — native zadržava živ socket preko reload-a (ista adresa = re-attach,
      // ne rekonekcija): nema prozora bez veze ni gubljenja auth/version/udid frame-ova.
      const snapshot = await this.crossApp.sendRawMessage(
        { action: 'ws.connect', data: { url: url } }, true, 5000, true) as WsBridgeState;

      if (!snapshot?.state) {
        throw new Error('invalid ws.connect response');
      }

      this.bridgeConnectFailures = 0;
      this.onBridgeState(snapshot);
    } catch {
      this.bridgeConnectFailures++;
      this.logger.warn('ws.connect failed (attempt ' + this.bridgeConnectFailures + ')');

      if (this.bridgeConnectFailures >= 2) {
        this.fallbackToWeb();
      } else {
        timer(2000).subscribe(() => this.bridgeAttach());
      }
    } finally {
      this.bridgeAttachPending = false;
    }
  }

  private fallbackToWeb() {
    this.logger.warn('WS bridge unavailable - falling back to web transport');

    // Best-effort: ako je native ipak otvorio socket a odgovor se izgubio, zatvori ga da
    // ne ostane dupli. Keš ne diramo — per-boot probe presuđuje pri sledećem startu.
    try {
      this.crossApp.sendRawMessage({ action: 'ws.close', data: { reason: 'bridge fallback' } });
    } catch { }

    this.lastBridgeState = null;
    this.transportMode = 'web';
    this.connectionPending = false;
    this.connect();
  }

  // Ogledalo web open/close observer-a za native socket. Na 'connected' NE diramo
  // `connected` subject — njega, kao i danas, podiže serverov `connected` frame
  // (stiže kroz ws.message → onMessageReceived → observe("connected") pretplata u ctor-u).
  // `pushed` = stvarna promena stanja koju je native gurnuo (ws.state); snapshot iz ws.connect
  // odgovora posle gašenja stare sesije ume da nosi zaostao `reason`, pa na njega ne reagujemo.
  private onBridgeState(s: WsBridgeState, pushed = false) {
    this.lastBridgeState = s;

    switch (s.state) {
      case 'connected':
        this.connectRetryCount.next(0);
        this.stopBridgeRetryCountdown();
        this.elapsedFromLastConnection$?.unsubscribe();
        this.elapsedFromLastConnection = 0;
        break;

      case 'connecting':
        this.stopBridgeRetryCountdown();
        break;

      case 'disconnected':
        this.logger.info('disconnected from WS (bridge). Reason: ' + (s.reason ?? 'unknown'));
        this.startNoConnectionTimer();
        if (this.connected.value) {
          this.connected.next(false); // kaskadno obara authenticated kroz postojeću skip(1) pretplatu
        }
        if (this.socketID) {
          this.socketID = "";
        }
        this.connectRetryCount.next(s.retryCount ?? (this.connectRetryCount.value + 1));
        this.startBridgeRetryCountdown(s.nextRetryInSec ?? 0);

        // Pukla je USPOSTAVLJENA veza sa serverom na koji nas je auth preusmerio — native bi se
        // vratio direktno na njega, a mi idemo na podrazumevani server da ponovo kaže gde firma pripada.
        if (pushed && s.reason === 'connection lost' && hasBackendServerOverride()) {
          this.revertToOriginalServer('lost');
        }
        break;
    }
  }

  // Countdown za no-connection UI — bridge varijanta istog bloka iz web reconnectDelay-a
  // (web putanja namerno netaknuta da browser aplikacije ne nose nikakav rizik).
  private startBridgeRetryCountdown(seconds: number) {
    this.retryCountdown$?.unsubscribe();

    if (seconds <= 0) {
      this.secondsToRetry = 0;
      return;
    }

    this.retryCountdown$ = timer(0, 1000).pipe(
      map(i => seconds - i),
      startWith(seconds),
      takeWhile(v => v >= 0)
    ).subscribe(val => this.secondsToRetry = val);
  }

  private stopBridgeRetryCountdown() {
    this.retryCountdown$?.unsubscribe();
    this.secondsToRetry = 0;
  }

  private buildWsUrl(): string {
    // environment.apiBase je getter (re-derivacija iz window.location + server override)
    return (environment.apiBase + 'ws').replace(/^http/, 'ws');
  }

  private connectWeb() {
    if (this.connectionPending) {
      return;
    }

    this.closeConnection();
    this.startNoConnectionTimer();


    const reconnectDelay = (error: any, retryCount?: number) => {
      var delayTime = Math.min(
        1000 * Math.pow(2, this.connectRetryCount.value),
        30000);// Exponential backoff with a max delay of 30 seconds

      this.logger.info('Retrying connection in ' + (delayTime / 1000) + ' seconds...');

      this.retryCountdown$?.unsubscribe();

      this.retryCountdown$ = timer(0, 1000).pipe(
        map(i => delayTime / 1000 - i),
        startWith(delayTime / 1000),                // emit 30 immediately without waiting 1s
        takeWhile(v => v >= 0)        // stop when reaches 0
      ).subscribe(
        (val) => {
          this.secondsToRetry = val;
        }
      );

      return timer(delayTime);
    }



    // environment.apiBase is a getter that re-derives from window.location on each access —
    // defeats any webpack bundle cache that might have inlined a stale value.
    const apiAddress = environment.apiBase + 'ws';
    var ws = of(apiAddress).pipe(
      // https becomes wss, http becomes ws
      map(apiUrl => apiUrl.replace(/^http/, 'ws')),
      switchMap(wsUrl => {
        this.logger.debug('WS connecting to: ' + wsUrl);
        this.connectionPending = true;
        this.retryCountdown$?.unsubscribe();
        this.secondsToRetry = 0;

        // Capture this specific socket so its open/close handlers can detect when they
        // belong to a connection that has already been superseded (e.g. during a server
        // switch, where the healthy old socket closes AFTER the new one has opened).
        // Da li je baš ovaj socket bio otvoren — razlikuje pad uspostavljene veze od neuspelog povezivanja.
        let opened = false;
        const conn = new WebSocketSubject<WebsocketData>({
          url: wsUrl,
          openObserver: {
            next: (val: any) => {
              if (this.connection !== conn) {
                return; // stale open — a newer connection already took over
              }
              opened = true;
              this.connectRetryCount.next(0);
              this.elapsedFromLastConnection$?.unsubscribe();
              this.elapsedFromLastConnection = 0;

              this.startHeartbeat();
            }
          },
          closeObserver: {
            next: (val: any) => {
              if (this.connection !== conn) {
                return; // stale close — must NOT stop the current connection's heartbeat
              }
              this.onDisconnect(val?.reason ?? 'unknown');
              this.connectionPending = false;

              if (this.socketID) {
                this.socketID = "";
              }

              this.logger.debug("WS fail count " + this.connectRetryCount.value);
              this.connectRetryCount.next(this.connectRetryCount.value + 1);

              // Pala USPOSTAVLJENA veza sa preusmerenim serverom — retry ne ide na njega nego na
              // podrazumevani server (isto kao u bridge grani onBridgeState).
              if (opened && hasBackendServerOverride()) {
                this.revertToOriginalServer('lost');
              }
            }
          }
        });

        this.connection = conn;
        return this.connection;

      }),
      retry({ count: Infinity, delay: reconnectDelay }),
      repeat({ count: Infinity, delay: reconnectDelay })
    );

    this.subscription = ws.subscribe({
      next: data => this.onMessageReceived(data),
      error: (err) => {
        console.error('WS error:', err);
      }
    });
  }


  private onDisconnect(reason: string) {
    this.logger.info('disconnected from WS. Reason: ' + reason);
    this.stopHeartbeat();
    this.startNoConnectionTimer();

    this.connected.next(false);
  }

  closeConnection() {
    this.subscription?.unsubscribe();
    this.connection?.complete();

    this.connected.next(false);
  }

  // Tear down the current connection and open a fresh one. connect() re-reads
  // environment.apiBase (a getter over deriveBackendOrigin), so any backend override
  // set in the meantime takes effect for the new WS URL and all future HTTP calls.
  reconnect() {
    if (this.transportMode === 'bridge') {
      // bridgeAttach sveže čita environment.apiBase: drugi URL (server-switch) znači da
      // native zatvara stari i otvara nov socket; isti URL prekida backoff (retry odmah).
      this.lastBridgeState = null;
      this.connectRetryCount.next(0);
      this.bridgeAttach();
      return;
    }

    this.stopHeartbeat();
    this.closeConnection();
    this.connectionPending = false;
    this.connectRetryCount.next(0);
    this.connect();
  }

  // Reacts to the `server` field of the WS auth response. Returns true if it initiated
  // a switch (caller should NOT mark the connection authenticated in that case).
  //   - empty `server`            → ignore (return false). Prazno = "za firmu ništa nije podešeno,
  //                                 uzmi iz podešavanja app-a" — ali samo odgovor PODRAZUMEVANOG
  //                                 servera odlučuje o rutiranju: preusmereni server ima svoju bazu
  //                                 i tamo firma obično nema podešen server (vratio bi nas nazad →
  //                                 ping-pong). Povratak na podrazumevani radi prekid veze
  //                                 (revertToOriginalServer 'lost'), gde se odluka ponovo pita.
  //   - already on target server  → ignore (return false); also breaks the loop since
  //                                 the new server reports its own name after switching
  //   - different server          → set/clear override + reconnect (return true)
  private handleServerSwitch(server?: string): boolean {
    const s = (server ?? '').trim();
    if (!s) {
      return false;
    }

    const lower = s.toLowerCase();
    const targetHost = lower.startsWith('app.') ? lower : 'app.' + lower;

    const currentHost = deriveBackendOrigin().host.toLowerCase();
    if (targetHost === currentHost) {
      // Workaround for Android wrapper <= 1.2.x: when the app goes to background the wrapper's
      // push channel re-connects the native socket to the server from its settings, while our
      // override still points at the target. Re-issue ws.connect so native moves back.
      if (this.isBridgeSocketOffTarget(targetHost)) {
        this.logger.warn('Native WS socket is on ' + this.lastBridgeState?.url + ' - reconnecting to ' + targetHost);
        this.reconnect();
        return true;
      }
      return false;
    }

    if (!this.allowServerSwitch()) {
      return false;
    }

    const naturalHost = deriveNaturalBackendOrigin().host.toLowerCase();
    if (targetHost === naturalHost) {
      clearBackendServerOverride();
    } else {
      setBackendServerOverride(targetHost);
    }

    this.logger.info('Switching backend server → ' + targetHost);
    this.reconnect();
    return true;
  }

  // Osigurač protiv petlje (A kaže „idi na B", B kaže „idi na A" — različite baze/keš): posle
  // MAX_SERVER_SWITCHES_PER_WINDOW prebacivanja u prozoru ostajemo gde jesmo do isteka prozora.
  private allowServerSwitch(): boolean {
    const now = Date.now();
    this.serverSwitchTimes = this.serverSwitchTimes.filter(t => now - t < SERVER_SWITCH_WINDOW_MS);

    if (this.serverSwitchTimes.length >= MAX_SERVER_SWITCHES_PER_WINDOW) {
      this.logger.error('WS server switch loop detected - staying on ' + deriveBackendOrigin().host);
      return false;
    }

    this.serverSwitchTimes.push(now);
    return true;
  }

  // True when the native-owned socket is connected to a host other than the one we expect.
  private isBridgeSocketOffTarget(targetHost: string): boolean {
    const url = this.transportMode === 'bridge' ? this.lastBridgeState?.url : null;
    if (!url) {
      return false;
    }
    try {
      return new URL(url).host.toLowerCase() !== targetHost;
    } catch {
      return false;
    }
  }

  // Counterpart to handleServerSwitch: drop the override and reconnect to the app's
  // original/natural backend, which re-issues the redirect through the auth `server` field if it
  // still applies. Same recovery a manual page reload performs (the override is in-memory, reset
  // on reload), but automatic and without losing app state. Two triggers:
  //   - 'lost'        → an ESTABLISHED connection to the switched server dropped (server restart,
  //                     admin "Prekini vezu", keepalive): go back right away, so a changed company
  //                     setting takes effect on the first reconnect instead of sticking to the target.
  //   - 'unreachable' → MAX_SERVER_SWITCH_RETRIES failed attempts to reach the switched server
  //                     (kept as a threshold so a dead target doesn't cause a tight switch loop).
  // Works for both transports: reconnect() re-reads environment.apiBase, so the web path opens a
  // fresh socket to the original server and the native bridge re-issues ws.connect with the
  // original URL (native then drops the old session for a fresh one).
  private revertToOriginalServer(trigger: 'lost' | 'unreachable') {
    if (this.revertPending) {
      return;
    }
    this.revertPending = true;

    // Defer out of the synchronous callback that bumped connectRetryCount (the web closeObserver
    // would otherwise tear the socket down mid-emit) and re-check on a settled state. timer(0)
    // fires long before the pending backoff retry to the switched server.
    timer(0).subscribe(() => {
      this.revertPending = false;

      if (!hasBackendServerOverride()) {
        return;
      }

      // A late success could have reset the counter meanwhile — don't abandon a server that
      // just recovered.
      if (trigger === 'unreachable' && this.connectRetryCount.value < MAX_SERVER_SWITCH_RETRIES) {
        return;
      }

      this.logger.warn(trigger === 'lost'
        ? 'WS connection to switched server lost - returning to original server to re-check the assignment'
        : 'WS unreachable after ' + this.connectRetryCount.value + ' attempts on switched server - reverting to original server');
      clearBackendServerOverride();
      this.reconnect();
    });
  }

  startHeartbeat() {
    // Heartbeat mechanism

    this.logger.debug('Heartbeat started');

    this.heartbeat$ = timer(HEARTBEAT_INTERVAL, HEARTBEAT_INTERVAL).pipe(
      // tap(() => console.log("heartbeat")),
      concatMap(() => {
        // Step 1: Send a ping
        if (this.logPing) {
          this.logger.trace("ping");
        }
        this.connection!.next({ type: 'ping', data: Date.now() });

        // Step 2: Wait for either a "pong" or a timeout
        return race(
          this.pong$
            .pipe(
              take(1),
              map(() => 'pong')
            ), // Wait for the "pong" message
          timer(HEARTBEAT_TIMEOUT)
            .pipe(
              map(() => 'timeout')
            ) // Wait for the timeout
        ).pipe(
          tap((result) => {
            if (result === 'pong') {
              if (this.logPing) {
                this.logger.trace("Pong");
              }

            } else if (result === 'timeout') {
              this.missedPongResponse++;
              this.logger.warn('Heartbeat timeout! No pong received. Miss no: ' + this.missedPongResponse);

              if (this.missedPongResponse > 2) {
                //this.connection?.error(new Error('Heartbeat timeout! No pong received.'));
                this.stopHeartbeat();
                this.closeConnection();

                this.connectionPending = false;
                this.connect();
              }
              //  this.subscription?.unsubscribe();
              //  this.connection?.complete();
            }
          }),
          catchError(() => {
            console.error('An error occurred during heartbeat.');
            return EMPTY;
          })
        );
      })
    );

    if (this.heartbeatSubscription && !this.heartbeatSubscription.closed) {
      this.heartbeatSubscription.unsubscribe();
    }

    // Subscribe to the heartbeat
    this.heartbeatSubscription = this.heartbeat$!.subscribe({
      error: (err) => console.error('Heartbeat error:', err),
      complete: () => console.log('Heartbeat completed.'),
    });
  }

  stopHeartbeat() {
    if (this.heartbeatSubscription && !this.heartbeatSubscription.closed) {
      this.heartbeatSubscription?.unsubscribe();
      this.logger.debug('Heartbeat stopped');
    }
  }


  observe(messageType: 'admin_orderCreated' | 'orderCreated' |
    'log' | 'event' |
    'sessionExpired' | 'orderStatusChanged' | 'table.summary' |
    'table' | 'socketDisonnected' |
    'auth' | 'socketMetadataChange' |
    'loginCompleted' |
    'companyIP' | 'orders' | 'companySettingsChanged' |
    "bannerChanged" | 'phoneCall' | 'orderPaid' | 'orderItemStatusChanged' | 'concurrentLimitReached' | 'sendNativeLog' |
    'checkVersion' | 'reload' | 'menuChanged' | 'config' | 'connected' | 'settings_get' | 'settings.get' | 'settings.set' | 'pong' | 'advertisementChanged' |
    'tablesChanged' | 'company.close' | 'company.open' | 'billing.servicesChanged' | 'signage.changed' |
    'ads.changed' | 'deviceProfile.changed' | 'service.message' | 'device.connection.alert' |
    'version' | 'setCompany'
  ): Subject<WebsocketResult> {
    this.connect();

    if (!this.subscriptions[messageType]) {
      this.subscriptions[messageType] = new Subject<WebsocketResult>();
    }

    return this.subscriptions[messageType];
  }


  onMessageReceived(data: any) {
    if (data.type != 'pong') {
      this.logger.debug("WS received: " + data.type);
    }

    if (this.subscriptions[data.type]) {
      var result: WebsocketResult = {
        success: data.success,
        data: data.data,
        requestID: data.requestID
      };


      (this.subscriptions[data.type]).next(result);
    }
  }


  send(data: WebsocketData) {
    if (!this.connected.value) {
      return; // ista drop semantika kao do sada — frame se šalje samo na živu konekciju
    }

    this.logger.debug('send WS type: ', data.type);

    if (this.transportMode === 'bridge') {
      this.crossApp.sendRawMessage({ action: 'ws.send', data: data });
    } else {
      this.connection!.next(data);
    }
  }

  private showServerID(serverID: string) {
    const isTestHost = window.location.hostname.endsWith('.test.emeni.rs');
    if (environment.production && !isTestHost) return;
    if (!this.debugEl) {
      this.debugEl = document.createElement('div');
      document.body.appendChild(this.debugEl);
    }
    // small, semi-transparent vertical label hugging the left edge, vertically centred —
    // out of the way of the bottom action buttons it used to overlap. Re-applied on every
    // call so tweaks take effect on the next WS (re)connect, not only first creation.
    Object.assign(this.debugEl.style, {
      position: 'fixed', left: '0', top: '50%',
      transform: 'translateY(-50%)',
      writingMode: 'vertical-rl', textOrientation: 'mixed',
      zIndex: '99999', background: 'rgba(0, 0, 0, 0.85)',
      color: '#fff', opacity: '1',
      fontSize: '13px', fontFamily: 'monospace', letterSpacing: '0.5px',
      padding: '8px 3px', borderRadius: '0 6px 6px 0',
      pointerEvents: 'none', userSelect: 'none'
    });
    this.debugEl.textContent = serverID;
  }

  runSleepModeDetection() {
    var lastTime = (new Date()).getTime();
    interval(2000).subscribe(() => {
      var currentTime = (new Date()).getTime();
      if (currentTime > (lastTime + 2000 * 2)) {  // ignore small delays
        this.connectRetryCount.next(0);
      }
      lastTime = currentTime;
    });
  }

}
